import React from 'react';
import { mergeStyleSets, mergeStyles } from '@fluentui/react';

import ItemRegistroTiempo from '../../models/ItemRegistroTiempo';

const partsClasses = mergeStyleSets({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '4px 0',
    },
    card: {
        display: 'flex',
        backgroundColor: '#ffffff',
        borderRadius: 10,
        boxShadow: '0 1px 5px rgba(0,0,0,0.07)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
        selectors: {
            ':hover': { boxShadow: '0 3px 12px rgba(0,0,0,0.12)', backgroundColor: '#fff8f7' },
        },
    },
    sidebar: {
        width: 5,
        flexShrink: 0,
    },
    cardInner: {
        flex: 1,
        minWidth: 0,
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    badge: {
        width: 44,
        height: 44,
        borderRadius: '50%',
        backgroundColor: 'var(--color-primario)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: 'white',
        fontSize: 11,
        fontWeight: 700,
    },
    content: {
        flex: 1,
        minWidth: 0,
    },
    times: {
        fontSize: 12,
        fontWeight: 600,
        color: '#1a1a1a',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    tag: {
        display: 'inline-block',
        backgroundColor: '#f0f0f0',
        color: '#555',
        fontSize: 10,
        fontWeight: 600,
        borderRadius: 4,
        padding: '1px 6px',
        marginLeft: 6,
        verticalAlign: 'middle',
        textTransform: 'uppercase' as const,
    },
    desc: {
        fontSize: 11,
        color: '#888',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        marginTop: 2,
    },
    hours: {
        flexShrink: 0,
        fontSize: 15,
        fontWeight: 700,
        color: 'var(--color-primario)',
        minWidth: 48,
        textAlign: 'right' as const,
    },
    summary: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 14px',
        fontSize: 12,
        color: '#555',
        fontWeight: 600,
        borderTop: '2px solid #ececec',
        backgroundColor: '#f8f8f8',
        borderRadius: '0 0 8px 8px',
        marginTop: 4,
    },
    emptyMsg: {
        padding: 16,
        color: '#aaa',
        fontSize: 13,
        textAlign: 'center' as const,
    },
});

const formatTime = (iso: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso.substring(0, 16).replace('T', ' ');
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) + ' '
        + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

interface IWorkOrderWorkPartsComponentProps {
    workParts: ItemRegistroTiempo[];
    onWorkPartClick?: (item: ItemRegistroTiempo) => void;
};

const WorkOrderWorkPartsComponent = (props: IWorkOrderWorkPartsComponentProps) => {
    const totalHours = props.workParts.reduce((sum: number, x: ItemRegistroTiempo) => {
        return sum + (x.TiempoEmpleado ? parseFloat(x.TiempoEmpleado.toString()) : 0);
    }, 0);

    if (props.workParts.length === 0) {
        return (
            <div className={partsClasses.emptyMsg}>
                Sin registros de tiempos en esta orden.
            </div>
        );
    }

    return (
        <div>
            <div className={partsClasses.container}>
                {props.workParts.map((item: ItemRegistroTiempo, index: number) => {
                    const isOpen = !item.Fin;
                    const sidebarColor = isOpen ? '#4CAF50' : '#2196F3';
                    return (
                        <div
                            key={item.Id || index}
                            className={partsClasses.card}
                            onClick={() => props.onWorkPartClick && props.onWorkPartClick(item)}>
                            <div className={mergeStyles(partsClasses.sidebar, { backgroundColor: sidebarColor })} />
                            <div className={partsClasses.cardInner}>
                                <div className={partsClasses.badge}>
                                    {index + 1}
                                </div>
                                <div className={partsClasses.content}>
                                    <div className={partsClasses.times}>
                                        {formatTime(item.Inicio)}
                                        {item.Fin ? <> → {formatTime(item.Fin)}</> : <span className={partsClasses.tag}>en curso</span>}
                                        {item.TipoTrabajo && <span className={partsClasses.tag}>{item.TipoTrabajo}</span>}
                                    </div>
                                    {item.Descripcion && (
                                        <div className={partsClasses.desc}>{item.Descripcion}</div>
                                    )}
                                </div>
                                <div className={partsClasses.hours}>
                                    {item.TiempoEmpleado ? parseFloat(item.TiempoEmpleado.toString()).toFixed(2) + 'h' : '—'}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className={partsClasses.summary}>
                <span>Registros: {props.workParts.length}</span>
                <span>Total: {totalHours.toFixed(2)} h</span>
            </div>
        </div>
    );
};

export default WorkOrderWorkPartsComponent;

